<template>
  <v-dialog
    v-model="relocation_dialog"
    persistent
    width="640"
    content-class="card relocation-confirm-dialog relocation-moved-dialog"
  >
    <div class="relocation-close">
      <v-icon size="24" @click="$emit('close-dialog')">mdi-close</v-icon>
    </div>

    <div class="text-center">
      <h2 v-if="!is_mobile">{{ heading }}</h2>
      <span v-else class="relocation-title">{{ heading }}</span>

      <k-spacing :y="is_mobile ? 2 : 4"></k-spacing>
      <span class="relocation-text relocation-intro">{{ intro }}</span>
    </div>

    <k-spacing :y="is_mobile ? 2 : 4"></k-spacing>

    <!--
      Van is het moment waarop de deelnemer zich aanmeldde, Naar waar de afspraak
      nu staat (ADR-0074). De tussenstops staan alleen in de mail — die verklaren
      waarom er een tweede mail ligt, en dat is een vraag die het scherm niet
      krijgt.
    -->
    <div class="relocation-moments">
      <div
        v-for="row in moments"
        :key="row.label"
        class="relocation-moment"
        :class="{ 'relocation-moment--stacked': is_mobile }"
      >
        <span class="relocation-moment__label">{{ row.label }}</span>
        <div class="relocation-moment__pill" :class="{ 'is-old': row.old }">
          <div class="relocation-moment__fields">
            <span class="relocation-moment__field relocation-moment__field--date">
              <v-icon size="20">mdi-calendar</v-icon>
              <span class="relocation-moment__text">{{ row.date }}</span>
            </span>
            <span class="relocation-moment__field relocation-moment__field--time">
              <v-icon size="20">mdi-clock-outline</v-icon>
              <span class="relocation-moment__text">{{ row.time }}</span>
            </span>
            <span
              v-if="showRoom"
              class="relocation-moment__field relocation-moment__field--room"
            >
              <v-icon size="20">mdi-map-marker-outline</v-icon>
              <span class="relocation-moment__text">{{ row.room }}</span>
            </span>
          </div>
          <v-icon
            size="24"
            class="relocation-moment__mark"
            :class="row.old ? 'is-from' : 'is-to'"
          >
            {{ row.old ? "mdi-close" : "mdi-check" }}
          </v-icon>
        </div>
      </div>
    </div>

    <k-spacing :y="is_mobile ? 4 : 8"></k-spacing>
    <div class="d-flex justify-center">
      <v-btn
        @click="accept(false)"
        variant="flat"
        class="btn text-white accept-btn"
        color="var(--company-primary)"
      >
        {{ $t("components.relocationDialog.accept") }}
      </v-btn>
    </div>
    <k-spacing :y="is_mobile ? 4 : 8"></k-spacing>
    <div
      class="d-flex align-center justify-center flex-wrap relocation-text relocation-fallback"
    >
      <i18n-t keypath="components.relocationDialog.fallback" tag="span">
        <template #change>
          <span @click="change_dialog = true" class="text-btn">
            {{ $t("components.relocationDialog.change") }}
          </span>
        </template>
        <template #unsubscribe>
          <span @click="delete_dialog = true" class="text-btn">
            {{ $t("components.relocationDialog.unsubscribe") }}
          </span>
        </template>
      </i18n-t>
    </div>
    <unsubscribeconfirmdialog
      :enroll="enroll"
      v-if="enroll.enroll_id && delete_dialog"
      :title="activityTitle"
      @delete="cancelEnroll"
      v-model="delete_dialog"
      @close-dialog="delete_dialog = false"
    />
    <change-activity-dialog
      @cancel-enroll="cancelEnroll"
      @update-enroll="updateEnroll"
      v-model="change_dialog"
      v-if="enroll.enroll_id && change_dialog"
      :enroll="enroll"
      awaiting-move-confirmation
      :page="mainstore.companypage"
      @close-dialog="
        (change_dialog = false), changed ? $emit('close-dialog') : ''
      "
    />
  </v-dialog>
</template>

<script>
import Unsubscribeconfirmdialog from "@/components/unsubscribeconfirmdialog.vue";
import changeActivityDialog from "@/components/dialogs/changeActivityDialog.vue";
import { useMainStore } from "../stores/store";
import api from "../services/api";
import config from "../config";
import isMobile from "@/mixins/isMobile";
import { toAmsterdamCalendarDate } from "@/util/DateUtil";
import {
  isMovedAgain,
  showsRoom,
  relocationMoments,
} from "@/util/RelocationMoments";

export default {
  mixins: [isMobile],
  components: {
    Unsubscribeconfirmdialog,
    changeActivityDialog,
  },
  props: {
    title: String,
    enroll: Object,
  },
  data: () => ({
    delete_dialog: false,
    changed: false,
    change_dialog: false,
    confirm_dialog: false,

    mainstore: useMainStore(),
  }),
  watch: {},
  computed: {
    confirmationData() {
      return this.enroll.confirmation_data || {};
    },
    activityTitle() {
      const content = this.enroll.content || {};

      return this.$i18n.locale == "nl"
        ? content.nl?.banner?.title
        : content.eng?.banner?.title;
    },
    movedAgain() {
      return isMovedAgain(this.confirmationData);
    },
    heading() {
      return this.$t(
        `components.relocationDialog.${this.movedAgain ? "heading_again" : "heading"}`,
        { activity: this.activityTitle }
      );
    },
    intro() {
      return this.$t(
        `components.relocationDialog.${this.movedAgain ? "intro_again" : "intro"}`
      );
    },
    showRoom() {
      return showsRoom(this.confirmationData, this.enroll.address?.room);
    },
    // De betekenis komt uit RelocationMoments; hier alleen de weergave.
    moments() {
      const labels = {
        from: this.$t("components.relocationDialog.from"),
        to: this.$t("components.relocationDialog.to"),
      };

      return relocationMoments(this.confirmationData, {
        date: this.enroll.date,
        start: this.enroll.start,
        end: this.enroll.end,
        room: this.enroll.address?.room,
      }).map((row) => ({
        label: labels[row.key],
        old: row.old,
        date: this.formatDate(row.date),
        time: `${row.start} − ${row.end}`,
        room: row.room || "-",
      }));
    },
  },

  methods: {
    // De epochs zijn Amsterdamse middernacht; een kale `new Date()` toont hier
    // de dag ervoor.
    formatDate(value) {
      return toAmsterdamCalendarDate(value).toLocaleDateString("nl-NL");
    },
    accept(canceled) {
      api
        .post(
          `${config.url.api}/v1/activities/${this.enroll.confirmation_reference}/confirmation`,
          {
            status: "accepted",
          }
        )
        .then((res) => {
          if (res.data.success) {
            if (!canceled) {
              this.$emit("close-dialog");

              this.$toast.success(
                this.$t("components.relocationDialog.accepted")
              );
            }
          }
        })
        .catch(() => {});
    },
    updateEnroll(item) {
      api
        .patch(
          `${config.url.api}/v1/activities/${item.enroll_id}/enroll`,
          item.date.slot
        )
        .then((res) => {
          if (res.data.success) {
            this.changed = true;
            this.accept(true);
          }
        })
        .catch(() => {});
    },
    cancelEnroll() {
      api
        .post(
          `${config.url.api}/v1/activities/${this.enroll.enroll_id}/unsubscribe`,
          this.enroll
        )
        .then((res) => {
          if (res.data.s == 12) {
            this.$toast.error(
              this.$t("views.template.enroll_update_cancel_text")
            );
          }
          if (res.data.success) {
            this.$toast.success(
              this.$t("views.template.enroll_update_cancel_succes")
            );
            this.accept(true);
            this.$emit("close-dialog");

            this.delete_dialog = false;
          }
        })
        .catch((err) => {
          switch (err.response.status) {
            case 400:
              this.$toast.error(
                this.$t("views.template.enroll_update_cancel_error")
              );
              break;

            default:
              this.$toast.error(
                this.$t("views.template.enroll_update_cancel_error")
              );
              break;
          }
        });
    },
  },
};
</script>
